// Template plays/assessments/debrief paragraphs: the fallback path that fires when the model is slow,
// unreachable, or fails validation. Spec §6 "fallbacks: template plays exist for every significant
// event ... the sim never blocks on the model." These must always be legal (validatePlay would otherwise
// reject the fallback too, leaving an advisor with nothing to show), so every order below is built from
// the same terrain and legality rules the sim itself uses. See plan Task 16.
import type { z } from 'zod';
import type { Grid } from '../sim/grid';
import type { Contact, Dials, Event, Graphic, Order, Unit } from '../sim/types';
import type { AssessmentSchema, DebriefSchema, PlaySchema } from './schemas';

/**
 * Context an advisor template (and the real prompt builder in Task 17) needs. The plan's interface list
 * for this type omits `grid`, but a legal MANEUVER/FIRES/RECON play needs a `point` target's grid cell,
 * and `isLegal` needs the grid to check passability - so `grid` is added here. Noted as a deviation in
 * the Task 16 report.
 */
export interface PlayContext {
  t: number;
  units: Unit[];
  contacts: Contact[];
  graphics: Graphic[];
  recentEvents: Event[];
  objectiveId: string;
  dials: Dials;
  grid: Grid;
}

export interface DebriefContext {
  events: Event[];
  orders: Order[];
  units: Unit[];
  lostTime: { item: string; minutes: number }[];
  stats: Record<string, number>;
}

type Play = z.infer<typeof PlaySchema>;
type PlayOrder = Play['orders'][number];
type Pt = { x: number; y: number };

function dist2(a: Pt, b: Pt): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** Closest point on segment [a,b] to p; falls back to a if the graphic is degenerate. */
function nearestPointOnSegment(p: Pt, a: Pt, b: Pt): Pt {
  const abx = b.x - a.x, aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return a;
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + abx * t, y: a.y + aby * t };
}

/** The point on a graphic nearest `near` - the nearest point on the line for a 2-point graphic (a phase
 * line), otherwise its first point (an objective, NAI, or assembly-area corner). Exported for reuse by
 * the FRAGO (fragmentary order) deterministic fallback parser (`src/ai/fragoParse.ts`), which needs the
 * same "aim at the named control graphic" math this module already has. */
export function graphicPoint(g: Graphic, near: Pt): Pt {
  if (g.pts.length >= 2) return nearestPointOnSegment(near, g.pts[0], g.pts[1]);
  return g.pts[0];
}

/** The nearest phase line `unit` has not yet crossed, or the objective graphic once all of them are. */
function nearestUncrossedGraphic(unit: Unit, graphics: Graphic[], objectiveId: string): Graphic | undefined {
  const uncrossed = graphics.filter((g) => g.kind === 'pl' && !unit.crossedPls.includes(g.id));
  if (uncrossed.length > 0) {
    const at = { x: unit.x, y: unit.y };
    return uncrossed.reduce((best, g) =>
      dist2(graphicPoint(g, at), at) < dist2(graphicPoint(best, at), at) ? g : best);
  }
  return graphics.find((g) => g.id === objectiveId);
}

/** Nearest passable cell to a point, for `move`/`attack`/`defend`/`withdraw` targets, which `isLegal`
 * requires to land on passable terrain. Searches outward in square rings from the point's own cell.
 * Exported for reuse by the FRAGO deterministic fallback parser (`src/ai/fragoParse.ts`). */
export function nearestPassableCell(grid: Grid, pt: Pt): number {
  const start = grid.cellOfCm(pt.x, pt.y);
  if (grid.passable(start)) return start;
  const cx = grid.cx(start), cy = grid.cy(start);
  const maxR = Math.max(grid.w, grid.h);
  for (let r = 1; r <= maxR; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const nx = cx + dx, ny = cy + dy;
        if (!grid.inBounds(nx, ny)) continue;
        const idx = grid.index(nx, ny);
        if (grid.passable(idx)) return idx;
      }
    }
  }
  return start; // no passable cell anywhere on the grid; isLegal will reject and validatePlay will drop it
}

/** In-bounds cell for a point target that does not require passability (fires/ISR impact/orbit points). */
function boundedCell(grid: Grid, pt: Pt): number {
  return grid.cellOfCm(pt.x, pt.y); // cellOfCm already clamps into [0, w) x [0, h)
}

/** Up to `limit` recent event ids to cite - significant ones preferred, so the template's citations are
 * as useful as an LLM's would be, and always known-valid since they come straight from `recentEvents`. */
function recentCitations(events: Event[], limit = 3): number[] {
  const significant = events.filter((e) => e.significant);
  const pool = significant.length > 0 ? significant : events;
  return pool.slice(-limit).map((e) => e.id);
}

function templateManeuver(ctx: PlayContext): Play {
  const candidates = ctx.units
    .filter((u) => u.alive && (u.type === 'armor' || u.type === 'mech') && u.orderId === -1)
    .sort((a, b) => b.strength - a.strength)
    .slice(0, 2);

  const orders: PlayOrder[] = candidates.map((u) => {
    const target = nearestUncrossedGraphic(u, ctx.graphics, ctx.objectiveId);
    const pt = target ? graphicPoint(target, { x: u.x, y: u.y }) : { x: u.x, y: u.y };
    const cell = nearestPassableCell(ctx.grid, pt);
    return { unitId: u.id, card: 'move', target: { kind: 'point', cell } };
  });

  return {
    name: candidates.length > 0 ? 'Push to the next phase line' : 'No uncommitted maneuver platoons',
    rationale: candidates.length > 0
      ? 'Move the two strongest uncommitted armor or mechanized platoons toward the nearest phase line not yet crossed, or the objective once all are, to keep tempo.'
      : 'Every armor and mechanized platoon is already committed; nothing to reorder this cycle.',
    // A standing template is by definition the same advice as last time (src/ai/schemas.ts).
    stance: 'continue',
    cited_event_ids: recentCitations(ctx.recentEvents),
    orders,
  };
}

/** Contact with the most other contacts within 600 m of it - a cheap density proxy for "densest cluster". */
function densestContactPoint(contacts: Contact[]): Pt | undefined {
  if (contacts.length === 0) return undefined;
  const RADIUS_CM = 60000; // 600 m
  let best = contacts[0], bestCount = -1;
  for (const c of contacts) {
    let n = 0;
    for (const o of contacts) if (dist2(c, o) <= RADIUS_CM * RADIUS_CM) n++;
    if (n > bestCount) { bestCount = n; best = c; }
  }
  return { x: best.x, y: best.y };
}

function nearestGraphic(graphics: Graphic[], near: Pt): Graphic | undefined {
  if (graphics.length === 0) return undefined;
  return graphics.reduce((best, g) => (dist2(graphicPoint(g, near), near) < dist2(graphicPoint(best, near), near) ? g : best));
}

function templateFires(ctx: PlayContext): Play {
  const arty = ctx.units
    .filter((u) => u.alive && u.type === 'spArty' && u.orderId === -1)
    .sort((a, b) => b.strength - a.strength)[0];
  const cluster = densestContactPoint(ctx.contacts);

  if (arty && cluster) {
    const cell = boundedCell(ctx.grid, cluster);
    return {
      name: 'Fire mission on the densest cluster',
      rationale: 'Mass indirect fire on the densest enemy contact cluster before it can concentrate against the advance.',
      stance: 'continue',
      cited_event_ids: recentCitations(ctx.recentEvents),
      orders: [{ unitId: arty.id, card: 'fireMission', target: { kind: 'point', cell } }],
    };
  }

  const recon = ctx.units.find((u) => u.alive && (u.type === 'recon' || u.type === 'uas') && u.orderId === -1);
  const nais = ctx.graphics.filter((g) => g.kind === 'nai');
  const nai = recon ? nearestGraphic(nais, { x: recon.x, y: recon.y }) : nais[0];

  return {
    name: recon && nai ? 'Observe the nearest NAI' : 'No fire mission available',
    rationale: recon && nai
      ? 'No contacts massed yet and no gun free; hold fires and watch the nearest named area of interest with a recon asset.'
      : 'No contacts to mass fire on and no artillery or recon platform free to observe.',
    stance: 'continue',
    cited_event_ids: recentCitations(ctx.recentEvents),
    orders: recon && nai ? [{ unitId: recon.id, card: 'observe', target: { kind: 'graphic', graphicId: nai.id } }] : [],
  };
}

function contactCoversGraphic(contacts: Contact[], g: Graphic): boolean {
  const radiusCm = (g.radiusM ?? 400) * 100;
  return contacts.some((c) => dist2({ x: c.x, y: c.y }, g.pts[0]) <= radiusCm * radiusCm);
}

function templateRecon(ctx: PlayContext): Play {
  const isr = ctx.units.filter((u) => u.alive && (u.type === 'recon' || u.type === 'uas') && u.orderId === -1);
  const orders: PlayOrder[] = [];

  const launcher = isr.find((u) => !u.uasAlive);
  if (launcher) {
    const nais = ctx.graphics.filter((g) => g.kind === 'nai');
    const uncontacted = nais.filter((g) => !contactCoversGraphic(ctx.contacts, g));
    const pool = uncontacted.length > 0 ? uncontacted : nais;
    const nai = nearestGraphic(pool, { x: launcher.x, y: launcher.y });
    if (nai) {
      const cell = boundedCell(ctx.grid, graphicPoint(nai, { x: launcher.x, y: launcher.y }));
      orders.push({ unitId: launcher.id, card: 'launchUas', target: { kind: 'point', cell } });
    }
  }

  const scout = isr.find((u) => u.id !== launcher?.id);
  if (scout) {
    const target = nearestUncrossedGraphic(scout, ctx.graphics, ctx.objectiveId);
    if (target) orders.push({ unitId: scout.id, card: 'recon', target: { kind: 'graphic', graphicId: target.id } });
  }

  return {
    name: orders.length > 0 ? 'Find before we commit' : 'No ISR asset available',
    rationale: orders.length > 0
      ? 'Launch the UAS toward the nearest NAI without a contact and send a scout to recon the route ahead; no decisive engagement until the enemy is located.'
      : 'No recon or UAS platform is free to task this cycle.',
    stance: 'continue',
    cited_event_ids: recentCitations(ctx.recentEvents),
    orders,
  };
}

/** The fallback play for one advisor persona. Always legal against `ctx.grid`/`ctx.graphics`/`ctx.units`
 * (see the per-persona functions above), so it can stand in for the model without validatePlay dropping
 * everything it contains. */
export function templatePlay(persona: 'maneuver' | 'fires' | 'recon', ctx: PlayContext): Play {
  switch (persona) {
    case 'maneuver': return templateManeuver(ctx);
    case 'fires': return templateFires(ctx);
    case 'recon': return templateRecon(ctx);
  }
}

const EVENT_PHRASE: Partial<Record<Event['kind'], (ev: Event) => string>> = {
  'contact.new': (ev) => `New enemy contact reported${ev.cell !== undefined ? ` near grid cell ${ev.cell}` : ''}.`,
  'contact.lost': (ev) => `A tracked contact${ev.cell !== undefined ? ` near grid cell ${ev.cell}` : ''} has been lost.`,
  'pl.crossed': () => 'A friendly element crossed a phase line.',
  'nai.contact': () => 'Activity observed in a named area of interest.',
  'obj.seized': () => 'The objective has been seized.',
  'unit.destroyed': () => 'A unit was destroyed.',
  'unit.broken': () => 'A unit broke contact and is withdrawing.',
  'unit.fuelLow': () => 'A unit is reporting low fuel.',
  'unit.ammoLow': () => 'A unit is reporting low ammunition.',
  'force.fuelLow': () => 'The force as a whole is approaching culmination on fuel.',
  'net.saturated': () => 'The command net is saturated; orders will be delayed.',
  'culminated': () => 'The force has culminated.',
  'sitrep': () => 'Routine situation report.',
};

/** The fallback S-2/SITREP-style assessment line for one event. */
export function templateAssessment(ev: Event, _ctx: PlayContext): z.infer<typeof AssessmentSchema> {
  const phrase = EVENT_PHRASE[ev.kind];
  const text = (phrase ? phrase(ev) : `Event: ${ev.kind} at t=${ev.t}.`).slice(0, 300);
  return { text, confidence: ev.significant ? 'medium' : 'low', cited_event_ids: [ev.id] };
}

/** The fallback debrief narrative: a summary paragraph from the run's stats plus one lost-time paragraph
 * when the analysis found any. Deterministic and always schema-valid, unlike the LLM path it stands in for. */
export function templateDebrief(ctx: DebriefContext): z.infer<typeof DebriefSchema> {
  const paragraphs: z.infer<typeof DebriefSchema>['paragraphs'] = [];
  const recentIds = ctx.events.slice(-5).map((e) => e.id);
  const statsLine = Object.entries(ctx.stats).map(([k, v]) => `${k}: ${v}`).join(', ');

  paragraphs.push({
    heading: 'Summary',
    text: (`The force issued ${ctx.orders.length} orders over the run. ${statsLine}`).slice(0, 600),
    cited_event_ids: recentIds,
  });

  if (ctx.lostTime.length > 0) {
    const total = ctx.lostTime.reduce((sum, lt) => sum + lt.minutes, 0);
    paragraphs.push({
      heading: 'Lost time',
      text: (`${total} minutes lost across ${ctx.lostTime.length} item(s): ` +
        ctx.lostTime.map((lt) => `${lt.item} (${lt.minutes} min)`).join('; ')).slice(0, 600),
      cited_event_ids: [],
    });
  }

  return { paragraphs: paragraphs.slice(0, 6) };
}
